// src/pages/Cash.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  openCashSession,
  closeCashSession,
  findOpenSession,
  expectedCashLive,
  listCashMoves,
  postCashMovement,
} from "../services/cashApi";
import { auth } from "../services/firebase";
import { onAuthStateChanged } from "firebase/auth";

const DEFAULT_LOCATION_ID = "boutique";
const PAGE_SIZE = 40;

const TYPE_LABEL = {
  open_float: "Fonds initial",
  sale_in: "Encaissement vente",
  change_out: "Monnaie rendue",
  adj_in: "Apport espèces",
  adj_out: "Retrait espèces",
  payout: "Sortie (dépense)",
};

function fmtAmount(n) {
  const num = Number(n || 0);
  const isNeg = num < 0;
  return `${isNeg ? "−" : ""}${Math.abs(num).toLocaleString()} XOF`;
}
function fmtDate(ts) {
  if (!ts) return "—";
  try {
    const d = ts?.toDate?.() || new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

export default function Cash() {
  const [session, setSession] = useState(null);
  const [openingFloat, setOpeningFloat] = useState(0);
  const [closingCount, setClosingCount] = useState(0);
  const [adj, setAdj] = useState(0);
  const [msg, setMsg] = useState("");

  const [user, setUser] = useState(null);

  // Solde attendu LIVE
  const [expected, setExpected] = useState(0);
  const unsubRef = useRef(null);

  // Journal
  const [moves, setMoves] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingMoves, setLoadingMoves] = useState(false);
  const [filterType, setFilterType] = useState("all");

  // --- Auth (récupérer la caissière actuelle) ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  const filteredMoves = useMemo(() => {
    if (filterType === "all") return moves;
    return moves.filter((m) => {
      const t = (m.type || "").toLowerCase();
      if (filterType === "sale") return t === "sale_in";
      if (filterType === "open") return t === "open_float";
      if (filterType === "change") return t === "change_out";
      if (filterType === "payout") return t === "payout";
      if (filterType === "adj") return t === "adj_in" || t === "adj_out";
      return true;
    });
  }, [moves, filterType]);

  async function reloadMoves(reset = false, sessionIdOverride = null) {
    const sid = sessionIdOverride || session?.id;
    if (!sid) return;
    setLoadingMoves(true);
    try {
      const res = await listCashMoves(sid, {
        pageSize: PAGE_SIZE,
        cursor: reset ? null : cursor,
      });
      if (reset) {
        setMoves(res.items);
      } else {
        setMoves((p) => [...p, ...res.items]);
      }
      setCursor(res.nextCursor || null);
    } catch (e) {
      setMsg("❌ Erreur journal: " + e.message);
    } finally {
      setLoadingMoves(false);
    }
  }

  async function refreshSession(currentUser) {
    // Pas d'utilisateur → on clear tout
    if (!currentUser) {
      setSession(null);
      setExpected(0);
      setMoves([]);
      setCursor(null);
      if (unsubRef.current) {
        try { unsubRef.current(); } catch { }
        unsubRef.current = null;
      }
      localStorage.removeItem("activeCashSessionId");
      localStorage.removeItem("activeCashSessionUid");
      return;
    }

    const cashierId = currentUser.uid;
    const s = await findOpenSession(DEFAULT_LOCATION_ID, { cashierId });
    setSession(s || null);

    // Nettoyer ancien abonnement live
    if (unsubRef.current) {
      try { unsubRef.current(); } catch { }
      unsubRef.current = null;
    }

    if (s?.id) {
      // On mémorise la session ET la caissière dans le localStorage
      localStorage.setItem("activeCashSessionId", s.id);
      localStorage.setItem("activeCashSessionUid", s.openedBy || cashierId);

      unsubRef.current = expectedCashLive(s.id, (val) => setExpected(val));
      await reloadMoves(true, s.id);
    } else {
      localStorage.removeItem("activeCashSessionId");
      localStorage.removeItem("activeCashSessionUid");
      setExpected(0);
      setMoves([]);
      setCursor(null);
    }
  }

  // Quand l’utilisateur (caissière) change → on recharge sa session
  useEffect(() => {
    refreshSession(user);
    return () => {
      if (unsubRef.current) {
        try { unsubRef.current(); } catch { }
        unsubRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function handleOpen() {
    try {
      const u = auth.currentUser;
      if (!u) {
        setMsg("❌ Utilisateur non connecté.");
        return;
      }
      await openCashSession({
        locationId: DEFAULT_LOCATION_ID,
        openingFloat: Number(openingFloat || 0),
        openedBy: u.uid,
      });
      setMsg("✅ Session ouverte");
      setOpeningFloat(0);
      await refreshSession(u);
    } catch (e) {
      setMsg("❌ " + e.message);
    }
  }

  async function handleClose() {
    try {
      if (!session) return;
      await closeCashSession({
        sessionId: session.id,
        closingCount: Number(closingCount || 0),
        closedBy: auth.currentUser?.uid || "anonymous",
      });
      setMsg("✅ Session fermée");
      setClosingCount(0);
      await refreshSession(auth.currentUser);
    } catch (e) {
      setMsg("❌ " + e.message);
    }
  }

  async function handleAdjIn() {
    try {
      if (!session) return;
      const amount = Number(adj || 0);
      if (amount <= 0) return;
      await postCashMovement({
        sessionId: session.id,
        locationId: DEFAULT_LOCATION_ID,
        type: "adj_in",
        amount,
        source: "MANUAL",
        note: "Apport espèces",
      });
      setAdj(0);
      await reloadMoves(true);
    } catch (e) {
      setMsg("❌ " + e.message);
    }
  }

  async function handleAdjOut() {
    try {
      if (!session) return;
      const amount = Number(adj || 0);
      if (amount <= 0) return;
      await postCashMovement({
        sessionId: session.id,
        locationId: DEFAULT_LOCATION_ID,
        type: "adj_out",
        amount: -amount,
        source: "MANUAL",
        note: "Retrait espèces",
      });
      setAdj(0);
      await reloadMoves(true);
    } catch (e) {
      setMsg("❌ " + e.message);
    }
  }

  const headerBadge = session
    ? <span className="badge bg-success">Session ouverte</span>
    : <span className="badge bg-secondary">Aucune session</span>;

  return (
    <div className="container-xxl">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="m-0">Caisse</h4>
        {headerBadge}
      </div>

      {!session ? (
        <div className="card">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label">Fonds initial</label>
                <input
                  type="number"
                  className="form-control"
                  value={openingFloat}
                  onChange={(e) => setOpeningFloat(e.target.value)}
                />
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleOpen}>
                  Ouvrir la session
                </button>
              </div>
            </div>
            <div className="small text-muted mt-2">{msg || "—"}</div>
          </div>
        </div>
      ) : (
        <>
          {/* Résumé + Ajustement + Fermeture (inchangé sauf que le solde est par session) */}
          {/* ... le reste de ton JSX (cards + journal) reste identique,
              juste avec reloadMoves(true) / reloadMoves(false) déjà adaptés ci-dessus ... */}

          {/* Bandeau résumé */}
          {session && (
            <div className="alert alert-info py-2 mb-3 small">
              Tu as déjà une session de caisse ouverte pour cet emplacement.
              Tu peux continuer avec cette session ou la fermer ci-dessous.
            </div>
          )}
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="fw-semibold mb-2">Session</div>
                  <div className="small">ID: <code>{session.id}</code></div>
                  <div className="small">Lieu: {session.locationId}</div>
                  <div className="small">
                    Ouverture: {(session.openingFloat || 0).toLocaleString()} XOF
                  </div>
                  <hr />
                  <div className="small text-muted">Solde attendu (LIVE)</div>
                  <div className="h5 m-0">{expected.toLocaleString()} XOF</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="fw-semibold mb-2">Ajustement espèces</div>
                  <input
                    type="number"
                    className="form-control mb-2"
                    placeholder="Montant"
                    value={adj}
                    onChange={(e) => setAdj(e.target.value)}
                  />
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-success" onClick={handleAdjIn}>
                      + Apport
                    </button>
                    <button className="btn btn-outline-danger" onClick={handleAdjOut}>
                      − Retrait
                    </button>
                  </div>
                  <div className="form-text mt-2">
                    Le solde attendu se met à jour automatiquement.
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="fw-semibold mb-2">Fermeture</div>
                  <label className="form-label">Montant compté</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={closingCount}
                    onChange={(e) => setClosingCount(e.target.value)}
                  />
                  <button className="btn btn-outline-dark" onClick={handleClose}>
                    Fermer la session
                  </button>
                  <div className="form-text mt-2">
                    Écart = Montant compté − Solde attendu.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Journal + filtres (inchangé, il utilise filteredMoves) */}
          {/* ... garde ton JSX existant pour le tableau des mouvements ... */}

          <div className="card mt-4 shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <h6 className="m-0">Journal des mouvements</h6>
                <div className="d-flex gap-2">
                  <select
                    className="form-select form-select-sm"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="open">Fonds initial</option>
                    <option value="sale">Encaissements vente</option>
                    <option value="change">Monnaie rendue</option>
                    <option value="adj">Ajustements</option>
                    <option value="payout">Sorties (dépenses)</option>
                  </select>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => reloadMoves(true)}
                    disabled={loadingMoves}
                  >
                    {loadingMoves ? "Chargement…" : "Rafraîchir"}
                  </button>
                </div>
              </div>

              {/* ... tableau comme tu l’avais ... */}

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th className="text-end">Montant</th>
                      <th>Source</th>
                      <th>Note</th>
                      <th>Réf. vente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMoves.map((m) => (
                      <tr key={m.id}>
                        <td className="small">{fmtDate(m.at)}</td>
                        <td className="small">
                          <span
                            className={
                              "badge " +
                              (m.amount < 0
                                ? "bg-danger-subtle text-danger"
                                : "bg-success-subtle text-success")
                            }
                          >
                            {TYPE_LABEL[m.type] || m.type}
                          </span>
                        </td>
                        <td
                          className={
                            "text-end " + (m.amount < 0 ? "text-danger" : "text-success")
                          }
                        >
                          {fmtAmount(m.amount)}
                        </td>
                        <td className="small">{m.source || "—"}</td>
                        <td className="small">{m.note || "—"}</td>
                        <td className="small">{m.saleId || "—"}</td>
                      </tr>
                    ))}
                    {!filteredMoves.length && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-3">
                          Aucun mouvement.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => reloadMoves(false)}
                  disabled={!cursor || loadingMoves}
                >
                  {loadingMoves ? "Chargement…" : cursor ? "Charger plus" : "Fin du journal"}
                </button>
              </div>
            </div>
          </div>

          <div className="small text-muted mt-2">{msg || "—"}</div>
        </>
      )}
    </div>
  );
}
